import { MDXProvider } from '@mdx-js/react';
import { createFileRoute } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { lazy, Suspense, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { DocsLayout } from '../../-components/DocsLayout';
import { mdxComponents } from '../../-components/MDXComponents';

const guideModules = import.meta.glob('../../-guides/*.docs.mdx');

function pathToSlug(path: string): string {
  const filename = path.split('/').pop() ?? '';
  return filename.replace('.docs.mdx', '');
}

const slugToModule: Record<string, () => Promise<unknown>> = {};
for (const [path, loader] of Object.entries(guideModules)) {
  slugToModule[pathToSlug(path)] = loader;
}

export const Route = createFileRoute('/docs/guides/$slug/')({
  component: GuideDocPage,
});

function GuideDocPage() {
  const { slug } = Route.useParams();

  const DocContent = useMemo(() => {
    const loader = slugToModule[slug];
    if (!loader) return null;
    return lazy(loader as () => Promise<{ default: React.ComponentType }>);
  }, [slug]);

  if (!DocContent) {
    return (
      <DocsLayout>
        <div className='flex flex-col items-center justify-center py-24 gap-3 text-center'>
          <BookOpen className='size-10 text-muted-foreground/30' />
          <p className='text-sm font-medium text-foreground'>
            No docs yet for <code className='text-primary'>{slug}</code>
          </p>
          <p className='text-xs text-muted-foreground max-w-xs'>
            Create{' '}
            <code className='text-muted-foreground'>
              routes/docs/-guides/{slug}.docs.mdx
            </code>{' '}
            to add this guide.
          </p>
        </div>
      </DocsLayout>
    );
  }

  return (
    <DocsLayout>
      <MDXProvider components={mdxComponents}>
        <Suspense fallback={<DocSkeleton />}>
          <DocContent />
        </Suspense>
      </MDXProvider>
    </DocsLayout>
  );
}

function DocSkeleton() {
  return (
    <div className='space-y-4'>
      <Skeleton className='h-9 w-48' />
      <Skeleton className='h-4 w-96' />
      <Skeleton className='h-4 w-80' />
      <div className='mt-8 space-y-3'>
        <Skeleton className='h-6 w-32' />
        <Skeleton className='h-32 w-full' />
      </div>
    </div>
  );
}
